import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { ExpirationDateComponent } from "./expirationDateComponent";

export const PassFailComponent = React.forwardRef((props, ref) => {
  const { name, defaultValue, disabled, read_only } = props;
  console.log(props);
  const [selectedOption, setSelectedOption] = useState(
    defaultValue || props.default || ""
  );
  
  // Helper to parse formatted date (e.g., "Jan 15th, 2025") to YYYY-MM-DD
  const parseFormattedDate = (dateStr) => {
    if (!dateStr) return "";
    // If already in YYYY-MM-DD format, return as-is
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return dateStr;
    
    try {
      // Try to parse formatted date like "Jan 15th, 2025"
      const dateMatch = dateStr.match(/([A-Za-z]{3})\s+(\d{1,2})(?:st|nd|rd|th)?,?\s+(\d{4})/);
      if (dateMatch) {
        const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
        const monthIndex = months.findIndex((m) => m === dateMatch[1]);
        const day = parseInt(dateMatch[2], 10);
        const year = parseInt(dateMatch[3], 10);
        if (monthIndex !== -1) {
          const date = new Date(year, monthIndex, day, 12, 0, 0);
          return date.toISOString().split("T")[0];
        }
      }
      // Try direct Date parsing as fallback
      const date = new Date(dateStr);
      if (!isNaN(date.getTime())) {
        return date.toISOString().split("T")[0];
      }
    } catch (e) {
      console.error("Error parsing date:", e);
    }
    return "";
  };
  
  // State for quantity tracking and per-unit expirations
  const [currentQuantity, setCurrentQuantity] = useState(
    props.data?.currentQuantity || props.data?.quantity || 0
  );
  const [unitExpirations, setUnitExpirations] = useState(
    props.data?.unitExpirations || []
  );
  
  // State for overall expiration date (used when hasExpiration is true - Quick Check items)
  const [overallExpiration, setOverallExpiration] = useState(
    parseFormattedDate(props.data?.expiration_date) || parseFormattedDate(props.data?.overallExpiration) || ""
  );

  const handleOptionChange = (event) => {
    if (props.disabled) {
      return;
    }
    setSelectedOption(event.target.value);
    
    // Notify parent of change
    if (props.onChange) {
      props.onChange(name, event.target.value);
    }
  };
  
  // Initialize quantity and unitExpirations from props or draft data
  useEffect(() => {
    // Check if we have draft data with currentQuantity and unitExpirations
    if (props.data?.currentQuantity !== undefined) {
      const draftQty = parseInt(props.data.currentQuantity, 10);
      if (draftQty !== currentQuantity) {
        setCurrentQuantity(draftQty);
      }
      // Initialize from draft unitExpirations if available
      if (props.data?.unitExpirations) {
        try {
          const draftExpirations = typeof props.data.unitExpirations === "string"
            ? JSON.parse(props.data.unitExpirations)
            : props.data.unitExpirations;
          if (Array.isArray(draftExpirations) && draftExpirations.length > 0) {
            setUnitExpirations(draftExpirations);
          }
        } catch (e) {
          console.error("Error parsing draft unitExpirations:", e);
        }
      }
    } else if (props.data?.quantity && currentQuantity === 0) {
      // Initialize from form quantity setting
      const qty = parseInt(props.data.quantity) || 0;
      setCurrentQuantity(qty);
      // Initialize unitExpirations array if requiresExpirationPerUnit
      if (props.data?.requiresExpirationPerUnit && qty > 0) {
        // Use default expiration dates from builder if available
        const defaultExpirations = props.data?.unitExpirationDates || [];
        const initialExpirations = Array(qty).fill("").map((_, index) => 
          defaultExpirations[index] || ""
        );
        setUnitExpirations(initialExpirations);
      }
    }
  }, [props.data?.quantity, props.data?.requiresExpirationPerUnit, props.data?.currentQuantity, props.data?.unitExpirations, props.data?.unitExpirationDates]);
  
  // Handle quantity changes
  const handleQuantityChange = (delta) => {
    if (props.disabled || props.read_only) return;
    
    const newQuantity = Math.max(0, currentQuantity + delta);
    setCurrentQuantity(newQuantity);
    
    // Adjust unitExpirations array to match new quantity
    if (props.data?.requiresExpirationPerUnit) {
      const newExpirations = [...unitExpirations];
      if (delta > 0) {
        // Adding units - add empty expiration dates
        for (let i = 0; i < delta; i++) {
          newExpirations.push("");
        }
      } else if (delta < 0) {
        // Removing units - remove from end
        newExpirations.splice(newQuantity, Math.abs(delta));
      }
      setUnitExpirations(newExpirations);
      
      // Update parent form data
      if (props.onChange) {
        props.onChange(name, selectedOption, {
          currentQuantity: newQuantity,
          unitExpirations: newExpirations,
        });
      }
    } else {
      // Update parent form data
      if (props.onChange) {
        props.onChange(name, selectedOption, {
          currentQuantity: newQuantity,
        });
      }
    }
  };
  
  // Handle per-unit expiration date changes
  const handleUnitExpirationChange = (index, date) => {
    if (props.disabled || props.read_only) return;
    
    // Ensure date is in YYYY-MM-DD format (date inputs return this format)
    const formattedDate = date || "";
    
    const newExpirations = [...unitExpirations];
    // Ensure array is long enough
    while (newExpirations.length <= index) {
      newExpirations.push("");
    }
    newExpirations[index] = formattedDate;
    setUnitExpirations(newExpirations);
    
    // Update parent form data
    if (props.onChange) {
      props.onChange(name, selectedOption, {
        currentQuantity: currentQuantity,
        unitExpirations: newExpirations,
      });
    }
  };

  const passRef = useRef(null);
  const failRef = useRef(null);
  const hiddenInputRef = useRef(null);
  const [passId, setPassId] = useState(null);
  const [failId, setFailId] = useState(null);

  useEffect(() => {
    // Assign unique IDs when the component mounts
    const newPassId = `passOption_${Math.random()
      .toString(36)
      .substr(2, 9)}`;
    const newFailId = `failOption_${Math.random()
      .toString(36)
      .substr(2, 9)}`;
    
    if (passRef.current) {
      passRef.current.id = newPassId;
      setPassId(newPassId);
    }
    if (failRef.current) {
      failRef.current.id = newFailId;
      setFailId(newFailId);
    }
    
    // Set up the ref for form validation - point to the hidden input
    if (ref && hiddenInputRef.current) {
      ref.current = hiddenInputRef.current;
    }
  }, [ref]);

  // Update the hidden input value and ref when selectedOption changes
  useEffect(() => {
    if (hiddenInputRef.current) {
      hiddenInputRef.current.value = selectedOption;
    }
    // Also update ref.current.value for form validation
    if (ref && ref.current) {
      ref.current.value = selectedOption;
    }
  }, [selectedOption, ref]);

  // Initialize ref on mount
  useLayoutEffect(() => {
    if (ref && hiddenInputRef.current) {
      ref.current = hiddenInputRef.current;
      // Set initial value
      if (ref.current) {
        ref.current.value = selectedOption;
      }
    }
  }, [ref, selectedOption]);

  const formatLabel = (label) => {
    return label;
  };

  // Check if this is a Quick Check item
  const isQuickCheck = name?.includes("Quick Check") || props.data?.field_name?.includes("Quick Check") || props.data?.label?.includes("Quick Check");

  return (
    <div className="form-group" style={{ position: "relative" }}>
      {isQuickCheck && (
        <span
          style={{
            position: "absolute",
            top: "0",
            right: "0",
            background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
            color: "white",
            fontSize: "11px",
            fontWeight: "500",
            padding: "5px 18px",
            whiteSpace: "nowrap",
            borderRadius: "20px",
            boxShadow: "0 2px 8px rgba(16, 185, 129, 0.35)",
            display: "flex",
            alignItems: "center",
            gap: "5px",
          }}
        >
          <span style={{ fontSize: "12px" }}>✓</span>
          Quick Check Item
        </span>
      )}
      <label className="form-label">
        <span
          dangerouslySetInnerHTML={{ __html: formatLabel(props.data.label) }}
        ></span>{" "}
{/* Show minimum quantity - supports new minQuantity field and backwards compat with old quantity field */}
        {(props.data.minQuantity > 0 || (props.data.quantity > 0 && !props.data.requiresExpirationPerUnit)) && !props.data.requiresExpirationPerUnit && (
          <span style={{ paddingLeft: "10px", color: "#ffffff", fontSize: "14px" }}>
            (Minimum quantity required: {props.data.minQuantity || props.data.quantity})
          </span>
        )}
        {props.data.required && (
          <span className="label-required badge badge-danger">Required</span>
        )}
      </label>
      <div className="custom-control custom-radio-passfail option-inline">
        <input
          ref={passRef}
          className="custom-control-input"
          name={name}
          type="radio"
          value="pass"
          checked={selectedOption === "pass"}
          onChange={handleOptionChange}
          disabled={disabled || read_only}
        />
        <label
          className="custom-control-label-passfail"
          htmlFor={passId}
        >
          Pass
        </label>
      </div>
      <div className="custom-control custom-radio-passfail option-inline">
        <input
          ref={failRef}
          className="custom-control-input"
          name={name}
          type="radio"
          value="fail"
          checked={selectedOption === "fail"}
          onChange={handleOptionChange}
          disabled={disabled || read_only}
        />
        <label
          className="custom-control-label-passfail"
          htmlFor={failId}
        >
          Fail
        </label>
      </div>
      {/* Quantity controls - show when quantity is set for lot tracking (requiresExpirationPerUnit) */}
      {props.data.quantity > 0 && props.data.requiresExpirationPerUnit && (
        <div className="mt-3">
          <label className="form-label" style={{ marginRight: "10px" }}>
            Current quantity:
          </label>
          <div style={{ display: "inline-flex", alignItems: "center", gap: "10px" }}>
            <span style={{ minWidth: "30px", textAlign: "center", fontWeight: "bold" }}>
              {currentQuantity}
            </span>
          </div>
        </div>
      )}
      
      {/* Per-unit expiration dates - show when requiresExpirationPerUnit is true */}
      {props.data.requiresExpirationPerUnit && currentQuantity > 0 && (
        <div className="mt-3">
          <label className="form-label">
            Expiration Date for each: {props.data.required && <span className="label-required badge badge-danger">Required</span>}:
          </label>
          {Array.from({ length: currentQuantity }).map((_, index) => {
            const lotPrefix = props.data?.lotLabelPrefix || "Lot";
            // Get the current expiration date - prioritize state, then builder defaults
            const currentExpiration = unitExpirations[index] || props.data?.unitExpirationDates?.[index] || "";
            // Convert formatted date to YYYY-MM-DD if needed (for date input)
            const getDateInputValue = (dateStr) => {
              if (!dateStr) return "";
              // If already in YYYY-MM-DD format, return as-is
              if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
                return dateStr;
              }
              // Try to parse formatted date like "Jan 15th, 2025"
              try {
                const dateMatch = dateStr.match(/([A-Za-z]{3})\s+(\d{1,2})(?:st|nd|rd|th),\s+(\d{4})/);
                if (dateMatch) {
                  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
                  const monthName = dateMatch[1];
                  const day = parseInt(dateMatch[2], 10);
                  const year = parseInt(dateMatch[3], 10);
                  const monthIndex = months.findIndex((m) => m === monthName);
                  if (monthIndex !== -1) {
                    const date = new Date(year, monthIndex, day, 12, 0, 0);
                    return date.toISOString().split("T")[0];
                  }
                }
                // Try direct Date parsing
                const date = new Date(dateStr);
                if (!isNaN(date.getTime())) {
                  return date.toISOString().split("T")[0];
                }
              } catch (e) {
                console.error("Error parsing date:", e);
              }
              return "";
            };
            
            return (
              <div key={index} className="mb-2" style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                <label style={{ minWidth: "80px", marginBottom: 0 }}>
                  {lotPrefix} {index + 1}:
                </label>
                <ExpirationDateComponent
                  {...props}
                  name={`${name}_unit_${index}_expiration`}
                  value={getDateInputValue(currentExpiration)}
                  defaultValue={getDateInputValue(currentExpiration)}
                  data={{
                    ...props.data,
                    label: "",
                    required: props.data.required,
                  }}
                  onChange={(expirationName, expirationDate) => {
                    // expirationDate from date input is already in YYYY-MM-DD format
                    handleUnitExpirationChange(index, expirationDate);
                  }}
                />
                {/* Hidden input for each unit's expiration date (for form submission) */}
                <input
                  type="hidden"
                  name={`${name}_unit_${index}_expiration`}
                  value={unitExpirations[index] || ""}
                />
              </div>
            );
          })}
          {/* Hidden input to store unitExpirations array as JSON - ensure it's always up to date */}
          <input
            type="hidden"
            name={`${name}_unitExpirations`}
            value={JSON.stringify(unitExpirations)}
            key={`unitExpirations-${unitExpirations.join("-")}`} // Force update when array changes
          />
          {/* Hidden input to store currentQuantity */}
          <input
            type="hidden"
            name={`${name}_currentQuantity`}
            value={currentQuantity}
            key={`currentQuantity-${currentQuantity}`} // Force update when quantity changes
          />
        </div>
      )}
      
      {/* Single expiration date - show when hasExpiration is true */}
      {props.data.hasExpiration && (
        <div className="mt-3">
          <ExpirationDateComponent
            {...props}
            name={`${name}_expiration`}
            value={overallExpiration}
            defaultValue={overallExpiration}
            data={{
              ...props.data,
              label: "Overall Expiration Date",
              required: props.data.required
            }}
            onChange={(expirationName, expirationDate) => {
              setOverallExpiration(expirationDate);
            }}
          />
        </div>
      )}
      {/* Hidden input for form validation - the form library checks this value */}
      <input
        type="hidden"
        ref={hiddenInputRef}
        name={name}
        value={selectedOption}
      />
    </div>
  );
});
